import React, { useState, useRef } from "react";
import { Shield, FileText, ChevronDown, CheckCircle2, AlertCircle } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";
import "./TermsAgreementModal.scss";

const TERMS_SECTIONS = [
  {
    title: "1. Acceptance of Terms",
    content:
      'By registering, accessing, or using the LMSCampus LMS platform, you (the "Institute" or "User") agree to be bound by these Terms and Conditions. If you do not agree, you must not use the platform.',
  },
  {
    title: "2. Account Responsibility",
    content: null,
    intro: "The Institute (Super Admin) is solely responsible for:",
    bullets: [
      "All users (students, instructors, admins) created under their account",
      "All content uploaded or shared on the platform",
      "All activities performed using their account",
    ],
    note: "The platform shall not be responsible for any misuse, unauthorized activity, or data shared by the institute or its users.",
  },
  {
    title: "3. Subscription & Pricing",
    content: null,
    bullets: [
      "All plans are valid only for the selected subscription duration (monthly/yearly).",
      "Prices are subject to change at any time.",
      "Any price changes will not affect active subscriptions.",
      "Once a plan expires, renewal will be charged at the updated pricing.",
    ],
  },
  {
    title: "4. Payments & Transaction Charges",
    content: "If the Institute uses the platform's payment gateway:",
    bullets: [
      "Payment gateway charges (e.g., Razorpay) will be deducted",
      "Applicable platform service fees will be deducted",
      "Taxes (GST or others) will be applied as per regulations",
    ],
    note: "The final settlement amount will be transferred after deducting all applicable charges.",
  },
  {
    title: "5. Platform Commission",
    content: "The platform shall charge a commission on each transaction processed through its payment system:",
    bullets: [
      "The commission is calculated as the higher of a fixed minimum fee (\u20B910 per transaction) or a percentage-based fee (7.5% of the transaction value) for the base plan.",
      "Applicable taxes (GST or others) will be charged in addition to the commission.",
      "Payment gateway charges (e.g., Razorpay fees) will be deducted separately.",
      "Commission rates may vary based on the institute's subscription plan and may be revised with prior notice.",
    ],
  },
  {
    title: "6. Revenue Sharing",
    content: null,
    bullets: [
      "Revenue generated from course sales (including self-paced courses) may be subject to revenue sharing.",
      "The revenue share percentage will be as agreed between the platform and the institute.",
      "The platform reserves the right to revise revenue-sharing terms with prior notice.",
    ],
  },
  {
    title: "7. Fair Pricing & Anti-Circumvention",
    content: null,
    bullets: [
      "Institutes shall not intentionally underprice courses on the platform or redirect users to external payment channels to avoid applicable platform fees or revenue sharing.",
      "The platform reserves the right to monitor pricing behavior and take necessary action, including suspension or termination of accounts, in case of violations.",
      "Any attempt to bypass the platform's payment system for transactions initiated through the platform will be considered a breach of these terms.",
    ],
  },
  {
    title: "8. Payouts",
    content: null,
    bullets: [
      "Payouts will be processed based on the defined settlement cycle (e.g., T+7 / T+15).",
      "Minimum payout thresholds may apply.",
      "Delays due to banking or third-party providers are not the responsibility of the platform.",
    ],
  },
  {
    title: "9. Storage & Usage Limits",
    content: null,
    bullets: [
      "Each subscription plan includes a predefined storage limit.",
      "If storage usage exceeds the allocated limit, additional charges will apply as per the current pricing.",
    ],
  },
  {
    title: "10. Content Ownership & Usage",
    content: null,
    bullets: [
      "The institute retains ownership of all content uploaded to the platform.",
      "By uploading content, the institute grants the platform a license to host, store, and deliver the content to users.",
      "The institute must ensure no copyrighted, illegal, or unauthorized content is uploaded.",
    ],
  },
  {
    title: "11. DRM & Content Protection",
    content: null,
    bullets: [
      "The platform may provide content protection mechanisms such as DRM or restricted access.",
      "However, the platform does not guarantee complete prevention of piracy or unauthorized access.",
      "The platform is not liable for content leaks or misuse outside its control.",
    ],
  },
  {
    title: "12. Data Privacy & Security",
    content: "The platform collects and processes user data for service delivery, analytics, and platform improvements.",
    bullets: [
      "The platform will not sell user data to third parties.",
      "The institute is responsible for obtaining consent from its users (students) for data collection.",
    ],
  },
  {
    title: "13. Student Data & Minor Users",
    content: null,
    bullets: [
      "If the institute enrolls students under the age of 18, the institute is solely responsible for obtaining parental or guardian consent for data collection and platform usage.",
      "The institute must comply with all applicable child data protection regulations in their jurisdiction.",
      "The platform is not liable for the institute's failure to obtain proper consent for minor users.",
    ],
  },
  {
    title: "14. AI Features & Third-Party Services",
    content: "The platform provides AI-powered features including mock interviews, AI-assisted learning, and coding practice:",
    bullets: [
      "AI-generated outputs (feedback, scores, suggestions) are provided for educational purposes and are not guaranteed to be fully accurate.",
      "AI features rely on third-party AI service providers whose availability is outside the platform's control.",
      "The platform is not responsible for temporary unavailability or changes in AI service quality due to third-party provider limitations.",
    ],
  },
  {
    title: "15. Zoom & Video Conferencing Integration",
    content: null,
    bullets: [
      "The platform integrates with Zoom and other third-party video conferencing services for live classes.",
      "Institutes must comply with Zoom's Terms of Service and acceptable use policies when using integrated video features.",
      "The platform is not responsible for Zoom outages, recording failures, or any disruptions caused by third-party video conferencing providers.",
    ],
  },
  {
    title: "16. WhatsApp & Communication Channels",
    content: null,
    bullets: [
      "The platform may provide WhatsApp and other messaging integrations for notifications and communication.",
      "Institutes must not use these channels for spam, unsolicited marketing, or any activity that violates messaging platform policies.",
      "The platform is not liable for message delivery failures caused by third-party communication providers.",
    ],
  },
  {
    title: "17. Fee Collection & Student Payments",
    content: null,
    bullets: [
      "Institutes using the built-in fee collection system are responsible for issuing proper invoices, receipts, and complying with applicable tax regulations.",
      "The platform acts only as a payment facilitator and is not a party to the financial relationship between the institute and its students.",
      "Any disputes between the institute and its students regarding payments must be resolved by the institute directly.",
    ],
  },
  {
    title: "18. Platform Availability",
    content: null,
    bullets: [
      "The platform does not guarantee uninterrupted or error-free operation.",
      "Temporary downtime may occur due to maintenance or technical issues.",
    ],
  },
  {
    title: "19. Prohibited Usage",
    content: "The institute agrees not to use the platform for:",
    bullets: [
      "Illegal, abusive, or fraudulent activities",
      "Uploading pirated or copyrighted content without permission",
      "Hosting misleading or scam courses",
      "Misuse of payment systems",
    ],
  },
  {
    title: "20. Suspension & Termination",
    content: "The platform reserves the right to suspend or terminate accounts without prior notice in case of:",
    bullets: [
      "Violation of terms",
      "Fraudulent activity",
      "Non-payment",
    ],
  },
  {
    title: "21. Refund Policy",
    content: null,
    bullets: [
      "The platform is not responsible for course refunds issued by institutes.",
      "Refund policies for students must be defined and managed by the institute.",
    ],
  },
  {
    title: "22. Intellectual Property",
    content: null,
    bullets: [
      "All platform branding, UI designs, source code, trademarks, and proprietary technology remain the exclusive property of VisibleCampus.",
      "Institutes may not replicate, reverse-engineer, redistribute, or rebrand any part of the platform.",
      "Custom branding features provided by the platform (logos, colors) are limited to the institute's own portal and do not transfer any ownership rights.",
    ],
  },
  {
    title: "23. Limitation of Liability",
    content: "The platform shall not be liable for:",
    bullets: [
      "Any direct or indirect losses",
      "Data loss or revenue loss",
      "Any damages arising from platform usage",
    ],
  },
  {
    title: "24. Indemnification",
    content: null,
    bullets: [
      "The institute agrees to indemnify, defend, and hold harmless VisibleCampus, its officers, directors, and employees from any claims, damages, losses, or expenses arising from the institute's content, user activities, or misuse of the platform.",
      "This includes any third-party claims resulting from the institute's violation of these terms or applicable laws.",
    ],
  },
  {
    title: "25. Changes to Terms",
    content: null,
    bullets: [
      "The platform reserves the right to update these Terms and Conditions at any time.",
      "Continued use of the platform implies acceptance of the updated terms.",
    ],
  },
  {
    title: "26. Dispute Resolution",
    content: null,
    bullets: [
      "Any disputes arising from the use of the platform shall first be resolved through good-faith mediation between the parties.",
      "If mediation fails, disputes shall be referred to binding arbitration under the Arbitration and Conciliation Act, 1996.",
      "The courts of Hyderabad, Telangana, India shall have exclusive jurisdiction.",
    ],
  },
  {
    title: "27. Governing Law",
    content:
      "These Terms shall be governed by and interpreted under the laws of India.",
  },
  {
    title: "28. Marketing & Branding",
    content:
      "The platform may use the institute's name and logo for promotional or marketing purposes unless explicitly requested otherwise.",
  },
];

const TermsAgreementModal = ({ onAccepted }) => {
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const contentRef = useRef(null);

  const handleScroll = () => {
    const el = contentRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (nearBottom) setScrolledToBottom(true);
  };

  const handleAccept = async () => {
    if (!checked) return;
    setLoading(true);
    setError("");
    try {
      await apiClient.put("/auth/accept-terms");
      // Update localStorage user
      const user = getCurrentUser();
      if (user) {
        user.termsAccepted = true;
        localStorage.setItem("user", JSON.stringify(user));
      }
      onAccepted();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to accept terms. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sa-terms-overlay">
      <div className="sa-terms-modal">
        {/* Header */}
        <div className="sa-terms-modal__header">
          <div className="sa-terms-modal__header-icon">
            <Shield size={24} />
          </div>
          <div>
            <h2 className="sa-terms-modal__title">Terms & Conditions</h2>
            <p className="sa-terms-modal__subtitle">
              Please review and accept the platform terms to continue
            </p>
          </div>
        </div>

        {/* Scrollable Content */}
        <div
          className="sa-terms-modal__content"
          ref={contentRef}
          onScroll={handleScroll}
        >
          <div className="sa-terms-modal__intro">
            <FileText size={18} />
            <span>LMS — Platform Agreement</span>
          </div>

          {TERMS_SECTIONS.map((section, idx) => (
            <div key={idx} className="sa-terms-section">
              <h3 className="sa-terms-section__title">{section.title}</h3>
              {section.intro && (
                <p className="sa-terms-section__text">{section.intro}</p>
              )}
              {section.content && (
                <p className="sa-terms-section__text">{section.content}</p>
              )}
              {section.bullets && (
                <ul className="sa-terms-section__list">
                  {section.bullets.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              )}
              {section.note && (
                <p className="sa-terms-section__note">{section.note}</p>
              )}
            </div>
          ))}
        </div>

        {/* Scroll indicator */}
        {!scrolledToBottom && (
          <div className="sa-terms-modal__scroll-hint">
            <ChevronDown size={16} />
            <span>Scroll to read all terms</span>
          </div>
        )}

        {/* Footer */}
        <div className="sa-terms-modal__footer">
          {error && (
            <div className="sa-terms-modal__error">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <label className="sa-terms-modal__checkbox">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              disabled={loading}
            />
            <span className="sa-terms-modal__checkmark" />
            <span className="sa-terms-modal__checkbox-label">
              I have read and agree to the Terms & Conditions
            </span>
          </label>

          <button
            className="sa-terms-modal__accept-btn"
            onClick={handleAccept}
            disabled={!checked || loading}
          >
            {loading ? (
              <>
                <span className="sa-terms-modal__spinner" />
                Processing...
              </>
            ) : (
              <>
                <CheckCircle2 size={18} />
                Accept & Continue
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsAgreementModal;
